import { useEffect, useState } from "react";
import {
  getPermisos,
  getPermisosByRol,
  assignPermisosToRol,
} from "../../../services/admin/permisos_admin";

function AsignarPermisosModal({ rol, onClose }) {
  const [permisos, setPermisos] = useState([]);
  const [checked, setChecked] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ alert del modal
  const [alert, setAlert] = useState(null); // { type: "success"|"error"|"info", message: string }

  const getNiceErrorMessage = (error, fallback = "Operation failed.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 220 ? clean.slice(0, 220) + "…" : clean;
  };

  const showAlert = (type, message, ms = 3500) => {
    setAlert({ type, message });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => setAlert(null), ms);
  };

  useEffect(() => {
    if (!rol?.rol_id) return;

    const loadData = async () => {
      setLoading(true);
      setAlert(null);

      try {
        const [allPermisos, permisosRol] = await Promise.all([
          getPermisos(),
          getPermisosByRol(rol.rol_id),
        ]);

        setPermisos(Array.isArray(allPermisos) ? allPermisos : []);
        setChecked(Array.isArray(permisosRol) ? permisosRol : []);
      } catch (err) {
        console.error("Error loading permissions for role:", err);
        setPermisos([]);
        setChecked([]);
        showAlert("error", getNiceErrorMessage(err, "Could not load permissions."));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [rol?.rol_id]);

  const toggleCheck = (id) => {
    setChecked((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const save = async () => {
    setSaving(true);
    setAlert(null);

    try {
      await assignPermisosToRol(rol.rol_id, checked);
      showAlert("success", "Permissions updated successfully.");
      // cerramos luego de un toque para que el user vea el success
      window.setTimeout(() => onClose?.(), 600);
    } catch (err) {
      console.error("Error updating permissions:", err);
      showAlert("error", getNiceErrorMessage(err, "Error updating permissions."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>

        <div style={headerRow}>
          <h2 style={title}>Assign Permissions – {rol?.nombre}</h2>
          <button onClick={onClose} style={closeBtn} title="Close">✕</button>
        </div>

        {/* ✅ ALERT */}
        {alert && (
          <div style={{ ...ui.alert, ...(alert.type === "error" ? ui.alertError : alert.type === "success" ? ui.alertSuccess : ui.alertInfo) }}>
            <span style={{ lineHeight: 1.3 }}>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)}>✕</button>
          </div>
        )}

        {/* LIST */}
        <div style={{ ...list, opacity: loading ? 0.7 : 1 }}>
          {loading ? (
            <div style={emptyState}>Loading permissions…</div>
          ) : permisos.length === 0 ? (
            <div style={emptyState}>No permissions found</div>
          ) : (
            permisos.map((p) => (
              <label key={p.permiso_id} style={item}>
                <input
                  type="checkbox"
                  checked={checked.includes(p.permiso_id)}
                  onChange={() => toggleCheck(p.permiso_id)}
                  style={checkbox}
                  disabled={saving}
                />
                <span>{p.descripcion}</span>
              </label>
            ))
          )}
        </div>

        <div style={buttonRow}>
          <button
            onClick={save}
            style={{ ...btnPrimary, opacity: saving || loading ? 0.7 : 1 }}
            disabled={saving || loading}
          >
            {saving ? "Saving…" : "Save"}
          </button>

          <button onClick={onClose} style={btnCancel} disabled={saving}>
            Cancel
          </button>
        </div>

      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  padding: "18px",
};

const modal = {
  width: "520px",
  background: "#FFFFFF",
  padding: "22px",
  borderRadius: "16px",
  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  border: "1px solid #E2E8F0",
  fontFamily: "Segoe UI, sans-serif",
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "10px",
};

const title = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 800,
  color: "#2F4779",
};

const closeBtn = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "20px",
  color: "#64748b",
  padding: "6px 8px",
  borderRadius: "8px",
};
closeBtn[":hover"] = { background: "#F1F5F9" };

const list = {
  maxHeight: "320px",
  overflowY: "auto",
  border: "1px solid #CBD5E1",
  padding: "12px",
  borderRadius: "10px",
  background: "#F8FAFC",
};

const item = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 0",
  fontSize: "14px",
  color: "#1E293B",
};

const checkbox = {
  width: "16px",
  height: "16px",
  cursor: "pointer",
};

const emptyState = {
  padding: "14px 6px",
  textAlign: "center",
  color: "#64748b",
  fontSize: "14px",
  fontWeight: 600,
};

const buttonRow = {
  display: "flex",
  gap: "12px",
  marginTop: "16px",
};

const btnPrimary = {
  flex: 1,
  padding: "12px",
  background: "#3B82F6",
  border: "none",
  borderRadius: "10px",
  color: "white",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 700,
};

const btnCancel = {
  flex: 1,
  padding: "12px",
  background: "#E2E8F0",
  border: "none",
  borderRadius: "10px",
  color: "#475569",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 700,
};

const ui = {
  alert: {
    margin: "10px 0 14px",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 700,
  },
  alertError: { background: "#FEF2F2", borderColor: "#FECACA", color: "#7F1D1D" },
  alertSuccess: { background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" },
  alertInfo: { background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1E3A8A" },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.75,
  },
};

export default AsignarPermisosModal;
